/**
 * Shared data helpers + hooks for the supporter role dashboards.
 * All calls are resilient (Promise.allSettled / try-catch) and non-financial.
 * Only NON-community-scoped endpoints are used, so any authenticated supporter
 * (not only VOLUNTEER_COMMUNITY members) gets real data or honest empty states.
 */
import { useEffect, useState } from 'react'
import api from '@/services/api'
import type { ApiResponse, PaginatedResponse } from '@/types'
import campaignService, { type Campaign } from '@/services/campaignService'
import {
  listResources,
  getSavedResources,
  type Resource,
} from '@/services/resourceService'
import notificationService from '@/services/notificationService'
import {
  getMySupporterRegistrations,
  type SupporterRegistration,
} from '@/services/supporterService'

/** Fetch active campaigns, optionally filtered client-side by campaign `type`. */
export async function fetchActiveCampaigns(
  types?: string[],
  size = 24,
): Promise<Campaign[]> {
  try {
    const page = await campaignService.getCampaigns({ status: 'ACTIVE', size })
    const items = page.content ?? []
    return types && types.length ? items.filter((c) => types.includes(c.type)) : items
  } catch {
    return []
  }
}

/** Fetch published resources by filter, returning the content array. */
export async function fetchResources(params: {
  topic?: string
  category?: string
  audience?: string
  type?: string
  size?: number
}): Promise<Resource[]> {
  try {
    const page = await listResources({ ...params, page: 0, size: params.size ?? 24 })
    return page.content ?? []
  } catch {
    return []
  }
}

export interface SupporterActivity {
  campaignsJoined: number
  resourcesSaved: number
  unreadCount: number
  registrationStatus: string | null
  registrations: SupporterRegistration[]
  loading: boolean
}

/** Compose activity metrics from real, non-community-scoped sources. */
export function useSupporterActivity(): SupporterActivity {
  const [state, setState] = useState<SupporterActivity>({
    campaignsJoined: 0,
    resourcesSaved: 0,
    unreadCount: 0,
    registrationStatus: null,
    registrations: [],
    loading: true,
  })

  useEffect(() => {
    let active = true
    Promise.allSettled([
      campaignService.getMyCampaigns({ size: 1 }),
      getSavedResources(0, 1),
      notificationService.getUnreadCount(),
      getMySupporterRegistrations(),
    ]).then(([camp, saved, unread, regs]) => {
      if (!active) return
      const registrations = regs.status === 'fulfilled' ? regs.value : []
      setState({
        campaignsJoined: camp.status === 'fulfilled' ? camp.value.totalElements ?? camp.value.content?.length ?? 0 : 0,
        resourcesSaved: saved.status === 'fulfilled' ? saved.value.totalElements ?? saved.value.content?.length ?? 0 : 0,
        unreadCount: unread.status === 'fulfilled' ? unread.value : 0,
        registrationStatus: registrations[0]?.status ?? null,
        registrations,
        loading: false,
      })
    })
    return () => { active = false }
  }, [])

  return state
}

/** Share a link via Web Share API, with clipboard-copy fallback. Returns true if shared/copied. */
export async function shareLink(url: string, title: string): Promise<boolean> {
  const absolute = url.startsWith('http') ? url : `${window.location.origin}${url}`
  try {
    if (navigator.share) {
      await navigator.share({ title, url: absolute })
      return true
    }
    await navigator.clipboard.writeText(absolute)
    return true
  } catch {
    return false
  }
}

/** Build the absolute front-end URL for a campaign detail page (for sharing). */
export function campaignShareUrl(c: Campaign): string {
  return `/campaigns/${c.id}`
}

/** Build the absolute front-end URL for a resource (for sharing). */
export function resourceShareUrl(r: Resource): string {
  return r.externalUrl || r.fileUrl || `/resources`
}

// Re-export for convenience
export { api, type ApiResponse, type PaginatedResponse }